import { EventType } from "@/analytics/analytics-events";
import { GeneratePDFReportEventProperties } from "@/analytics/pdf-report-events-properties";
import { fetchDistanceUnitSetting } from "@/components/common/settings-context/distance-unit-utils";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { hideLightweightElements } from "@/store/lightweight-elements-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { deactivateTool } from "@/store/ui/ui-slice";
import { selectCurrentUser } from "@/store/user-selectors";
import { useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  convertToDateString,
  DistanceUnitTypes,
  GUID,
  sanitizeFileName,
} from "@faro-lotv/foundation";
import {
  hideIElements,
  selectProjectId,
  selectProjectName,
} from "@faro-lotv/project-source";
import {
  Img360Mode,
  ReportDateFormat,
  ReportSheetContent,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";

interface CreateReportParams {
  /** The leaves selected in the capture tree */
  selectedLeaves: GUID[];
  /** The report sheet content to include */
  content: ReportSheetContent[];
  /** Number of 2D images */
  img2dCount: number;
  /** Number of 360 images */
  img360Count: number;
  /** Number of scan 360s (single-scan-section leaves, excluding video-walk 360s) */
  scan360Count: number;
  /** Whether to include hyperlinks */
  includeHyperlinks: boolean;
  /** Whether to include page numbers */
  includePageNumbers: boolean;
  /** Whether to include the overview page */
  includeOverviewPage: boolean;
  /** Whether to include tags */
  includeTags: boolean;
  /** Whether to include annotations */
  includeAnnotations: boolean;
  /** Whether to include AI descriptions */
  includeAiDescriptions: boolean;
  /** The 360 image mode */
  img360Mode: Img360Mode;
  /** The PDF quality setting */
  pdfQuality: "web" | "print";
  /** The locale for the report */
  locale: string;
  /** The date format for the report */
  dateFormat: ReportDateFormat;
}

interface UseCreateReportReturn {
  /** Trigger report creation */
  createReport(): Promise<void>;
  /** Whether report creation is in progress */
  isProcessing: boolean;
}

/**
 * Hook that creates the report and informs the user about the progress
 *
 * @returns a function that creates the report and a boolean indicating whether the report creation is in progress.
 */
export function useCreateReport({
  selectedLeaves,
  content,
  img2dCount,
  img360Count,
  scan360Count,
  includeHyperlinks,
  includePageNumbers,
  includeOverviewPage,
  includeTags,
  includeAnnotations,
  includeAiDescriptions,
  img360Mode,
  pdfQuality,
  locale,
  dateFormat,
}: CreateReportParams): UseCreateReportReturn {
  const currentUser = useAppSelector(selectCurrentUser);
  const projectId = useAppSelector(selectProjectId);
  const projectName = useAppSelector(selectProjectName);

  const { handleErrorWithToast } = useErrorHandlers();
  const dispatch = useAppDispatch();
  const { pointCloudApiClient, coreApiClient, settingsApiClient } =
    useApiClientContext();
  const { openToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);

  const createReport = useCallback(async () => {
    if (!currentUser?.id || !projectId || !selectedLeaves.length) {
      return;
    }

    setIsProcessing(true);

    // Fetch the latest distance unit setting for the report
    const measurementUnitType =
      (await fetchDistanceUnitSetting(settingsApiClient)) ??
      DistanceUnitTypes.meters;

    const useHqImages = pdfQuality === "print";

    // Fetch the company logo at report creation time.
    // Errors are silently ignored since the custom logo is not critical and uses SphereXG logo as fallback.
    let customLogoUrl: string | undefined;
    try {
      const res = await coreApiClient.getCompanyLogo(projectId);
      customLogoUrl = res.logoUrl;
    } catch {
      customLogoUrl = undefined;
    }

    Analytics.track<GeneratePDFReportEventProperties>(
      EventType.generatePDFReport,
      {
        numberOf360Images: img360Count,
        numberOf2dImages: img2dCount,
        numberOfScan360s: scan360Count,
        includeLinks: includeHyperlinks,
        includePageNumbers,
        includeOverviewPage,
        includeTags,
        includeAnnotations,
        includeAiDescriptions,
        img360Mode,
        useHqImages,
        hasCustomLogo: !!customLogoUrl,
        locale,
        dateFormat,
        measurementUnitType,
      },
    );

    try {
      await pointCloudApiClient.generateReport({
        userId: currentUser.id,
        projectId,
        content,
        includeHyperlinks,
        includePageNumbers,
        includeOverviewPage,
        includeAiDescriptions,
        includeTags,
        useHqImages,
        fileName: sanitizeFileName(
          `${projectName}_${convertToDateString(DateTime.now().toISO())}`,
        ),
        img360Mode,
        customLogoUrl,
        locale,
        dateFormat,
        measurementUnitType,
      });

      openToast({
        variant: "info",
        title: "Report is being created",
        message:
          "The download will automatically start when the file is ready. Check the progress in the Cloud Activity.",
      });

      dispatch(deactivateTool());
      dispatch(hideIElements([]));
      dispatch(hideLightweightElements([]));
    } catch (e) {
      handleErrorWithToast({
        title: "Report generation failed",
        error: e,
      });
    }

    setIsProcessing(false);
  }, [
    currentUser?.id,
    projectId,
    selectedLeaves.length,
    settingsApiClient,
    pdfQuality,
    img360Count,
    img2dCount,
    scan360Count,
    includeHyperlinks,
    includePageNumbers,
    includeOverviewPage,
    includeTags,
    includeAnnotations,
    includeAiDescriptions,
    img360Mode,
    locale,
    dateFormat,
    coreApiClient,
    pointCloudApiClient,
    content,
    projectName,
    openToast,
    dispatch,
    handleErrorWithToast,
  ]);

  return { createReport, isProcessing };
}
